/**
 * Compare-sales dialog body. Thin orchestrator:
 *  1. Resolves loading / empty states.
 *  2. Runs ``useCompareSalesData`` to derive everything the table and
 *     payments box render.
 *  3. Renders ``CompareSalesTable`` followed by ``CompareSalesPaymentsBox``
 *     with a 3px visual gap so the two read as one surface.
 *
 * Derivations, pairing logic, and per-row rendering live in siblings -
 * see ``useCompareSalesData``, ``CompareSalesTable`` and
 * ``CompareSalesPaymentsBox``.
 */
import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { CompareSalesResponse } from '../../../../services/inspector/api'
import { useCompareSalesData } from './useCompareSalesData'
import CompareSalesTable from './CompareSalesTable'
import CompareSalesSkeleton from './CompareSalesSkeleton'
import { COMPARE_NUM_FONT_SX } from '../../../../components/inspector/shared/compareSalesPalette'

interface CompareSalesViewProps {
  data?: CompareSalesResponse
  loading?: boolean
  invoiceNumber?: string
  xeroReference?: string
  xeroInvoiceNumber?: string
  lightspeedSaleLink?: string
  xeroInvoiceLink?: string
  // Lifted up to the dialog so the toggle can sit alongside the
  // summary chips in the title bar.
  showDiffsOnly: boolean
}

const CompareSalesView: React.FC<CompareSalesViewProps> = ({
  data,
  loading = false,
  invoiceNumber,
  xeroReference,
  xeroInvoiceNumber,
  lightspeedSaleLink,
  xeroInvoiceLink,
  showDiffsOnly,
}) => {
  // Notes start collapsed; each side toggles independently because
  // the LS and Xero notes are peer "line items" - the analyst might
  // care about one side's note without expanding the other.
  const [lsNotesExpanded, setLsNotesExpanded] = useState(false)
  const [xeroNotesExpanded, setXeroNotesExpanded] = useState(false)
  const compareData = useCompareSalesData(data, showDiffsOnly)

  if (loading) {
    return <CompareSalesSkeleton />
  }

  if (!compareData) {
    return (
      <Box sx={{ py: 2, px: 3 }}>
        <Typography variant="body2" color="text.secondary">
          No line item data available
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      className="compare-sales-view"
      sx={{
        pt: 1.5,
        pb: 2,
        px: 2,
        // Single source of truth for the Compare view's font stack:
        // applies Inter + tabular-nums to the root AND cascades it to
        // every Typography / link / Chip label / input / button
        // underneath. MUI Typography normally sets its own fontFamily
        // from the theme so a plain ``fontFamily`` inheritance on the
        // root wouldn't reach those elements - the descendant selector
        // here overrides them in one shot. Update
        // ``COMPARE_NUM_FONT_SX`` and the whole surface updates.
        ...COMPARE_NUM_FONT_SX,
        // Include ``span`` so component="span" Boxes (e.g. the payment
        // entry label inside a ``Less:`` row) inherit the font too.
        // Inside the compare-sales-view scope this is safe - every
        // span here is a label / value / chip, never a different font
        // by design.
        '& .MuiTypography-root, & a, & span, & .MuiChip-label, & input, & button, & .MuiLink-root':
          {
            ...COMPARE_NUM_FONT_SX,
          },
      }}
    >
      <CompareSalesTable
        data={compareData}
        showDiffsOnly={showDiffsOnly}
        lsNotesExpanded={lsNotesExpanded}
        xeroNotesExpanded={xeroNotesExpanded}
        onToggleLsNotes={() => setLsNotesExpanded(v => !v)}
        onToggleXeroNotes={() => setXeroNotesExpanded(v => !v)}
        invoiceNumber={invoiceNumber}
        xeroReference={xeroReference}
        xeroInvoiceNumber={xeroInvoiceNumber}
        lightspeedSaleLink={lightspeedSaleLink}
        xeroInvoiceLink={xeroInvoiceLink}
      />
    </Box>
  )
}

export default CompareSalesView
